/**
 * PrecisionToggle — A small toggle button that switches between
 * 'cents' (2 decimal places) and 'precision' (5 decimal places) display.
 *
 * The mode is persisted in localStorage and triggers a re-render of
 * any component using the formatCents() utility.
 */
import React, { useState, useCallback } from 'react';
import { getPrecisionMode, setPrecisionMode, type PrecisionMode } from '../utils/formatCents';

export function PrecisionToggle({ className = '' }: { className?: string }) {
  const [mode, setMode] = useState<PrecisionMode>(getPrecisionMode());

  const toggle = useCallback(() => {
    const newMode: PrecisionMode = mode === 'cents' ? 'precision' : 'cents';
    setPrecisionMode(newMode);
    setMode(newMode);
    // Force a page re-render so all formatCents() calls pick up the new mode
    window.dispatchEvent(new Event('precision-mode-changed'));
  }, [mode]);

  return (
    <button
      onClick={toggle}
      className={`inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 ${className}`}
      title={mode === 'cents' ? 'Showing 2 decimal places. Click for 5 decimal precision.' : 'Showing 5 decimal places. Click for 2 decimal places.'}
    >
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {mode === 'cents' ? '¢' : '0.00000'}
    </button>
  );
}

/**
 * Hook that re-renders the component when the precision mode changes.
 * Use this in pages that display formatted cents values.
 */
export function usePrecisionMode(): PrecisionMode {
  const [mode, setMode] = useState<PrecisionMode>(getPrecisionMode());
  React.useEffect(() => {
    const handler = () => setMode(getPrecisionMode());
    window.addEventListener('precision-mode-changed', handler);
    return () => window.removeEventListener('precision-mode-changed', handler);
  }, []);
  return mode;
}
